import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import Image from 'next/image'
import cx from 'classnames'

import Layout from '@/components/Layout'
import GoogleLoginButton from '@/components/GoogleLoginButton'
import { getMessage } from '@/utils/messages'
import { getNeatClassName } from '@/utils/css'
import { URLS, SSO_SCOPE, INTERNAL_QUERY_PARAMS, LOGIN_TYPE } from '@/constants'
import config from '@/config'
import useRoutes from '@/hooks/useRoutes'
import { toQS } from '@/utils/url'
import useScope from '@/hooks/useScope'
import Link from '@/components/common/Link'
import useRecaptchaBadgePosition from '@/hooks/useRecaptchaBadgePosition'
import { getShouldSkipRecaptcha } from '@/utils/neat'
import { getUserName } from '@/utils/user'
import useAuth from '@/hooks/useAuth'

import styles from './LoginPage.module.scss'
import LoginForm from './LoginForm'
import ReCaptcha from './ReCaptcha'
import Copyright from './Copyright'

const isAppleSsoEnabled = false

const RECAPTCHA_TEST_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'

export default function SignInLayout({
  onSubmit,
  isLoading,
  onGoogleLoginSuccess,
  schema,
  isScanSnap = false,
  recaptchaProps,
  loginType = LOGIN_TYPE.signIn,
  initialUsername,
  usernameDisabled = false,
}) {
  const routes = useRoutes()
  const { isIOSClient, isWebClient, isMobileClient, isDesktopClient, isOauthClient } = useScope()
  const isForInvitation = loginType === LOGIN_TYPE.invitation
  const isIntuitSsoEnabled = (isWebClient || isScanSnap || isDesktopClient || isOauthClient) && !isForInvitation
  const isGoogleSsoEnabled = !isIOSClient && !isForInvitation
  const hasSsoButton = isGoogleSsoEnabled || isIntuitSsoEnabled || isAppleSsoEnabled
  const recaptchaBagePosition = useRecaptchaBadgePosition()
  const shouldSkipRecaptcha = getShouldSkipRecaptcha(isMobileClient)
  const { canStoreUsername, shouldShowRememberMe } = useAuth()

  const intuitSSOUrl = useMemo(() => {
    if (!isIntuitSsoEnabled) {
      return null
    }
    const callbackUrl = `${config.app.url}${routes.ssoSuccess.url()}`
    const linkCallbackUrl = `${config.app.url}${routes.ssoLogin.url({ [INTERNAL_QUERY_PARAMS.accountExists]: 'true', [INTERNAL_QUERY_PARAMS.ssoScope]: SSO_SCOPE.INTUIT })}`

    const params = {
      callback_url: encodeURIComponent(callbackUrl),
      link_callback_url: encodeURIComponent(linkCallbackUrl),
    }

    return `${URLS.ssoIntuit}&${toQS(params)}`
  }, [isIntuitSsoEnabled, routes])

  const getInitialUsername = () => {
    if (initialUsername) return initialUsername
    return canStoreUsername ? getUserName() || '' : ''
  }
  return (
    <>
      <Layout.Content isLogin className={cx(styles.isLogin, getNeatClassName('login-page'))}>
        <Formik
          initialValues={{ username: getInitialUsername(), password: '' }}
          onSubmit={onSubmit}
          validationSchema={schema}
          enableReinitialize={isForInvitation}
        >
          <LoginForm
            isLoading={isLoading}
            loginType={loginType}
            recaptchaError={recaptchaProps.recaptchaError}
            submitDisabled={recaptchaProps.isRecaptchaVerifying}
            disabled={usernameDisabled}
          />
        </Formik>
        <div>
          {hasSsoButton && (
          <>
            <div className={cx(styles.divider, shouldShowRememberMe && styles.withRememberMe)}>
              {getMessage('login.dividerText')}
            </div>
            <div className={styles.sso}>
              {isGoogleSsoEnabled && <GoogleLoginButton onSuccess={onGoogleLoginSuccess} />}
              {isAppleSsoEnabled && (
                // @TODO properly configure the link or button when apple sso is enabled
                <a href={intuitSSOUrl} className={styles.ssoButton}>
                  <Image src="/sign-in-with-apple.svg" width={280} height={40} alt="Sign in with Apple" />
                </a>
              )}
              {isIntuitSsoEnabled && (
                <a href={intuitSSOUrl} className={styles.ssoButton}>
                  <Image src="/sign-in-with-intuit.svg" width={280} height={38} style={{ objectFit: 'cover' }} alt="Sign in with Intuit" />
                </a>
              )}
            </div>
          </>
          )}
        </div>
        {(!isMobileClient || isForInvitation) && !isOauthClient && (
          <div className={styles.signUpLink}>
            <Link
              to={isForInvitation ? routes.createAccount.url({ [INTERNAL_QUERY_PARAMS.createAccountSource]: 'invitation' }) : URLS.signUp}
              size="xsmall"
              external={!isForInvitation}
              openTab={!isForInvitation}
            >
              {getMessage('login.signUpLinkText')}
            </Link>
          </div>
        )}
        {recaptchaProps.enabled && (
        <>
          {recaptchaBagePosition === 'inline' && <Copyright className={styles.copyright} />}
          <ReCaptcha
            {...recaptchaProps}
            badge={recaptchaBagePosition}
            siteKey={shouldSkipRecaptcha ? RECAPTCHA_TEST_SITE_KEY : undefined}
          />
        </>
        )}
      </Layout.Content>
    </>
  )
}

SignInLayout.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onGoogleLoginSuccess: PropTypes.func.isRequired,
  schema: PropTypes.shape({}).isRequired,
  isScanSnap: PropTypes.bool,
  loginType: PropTypes.oneOf(Object.values(LOGIN_TYPE)),
  initialUsername: PropTypes.string,
  usernameDisabled: PropTypes.bool,
  recaptchaProps: PropTypes.shape({
    recaptchaError: PropTypes.string,
    isRecaptchaVerifying: PropTypes.bool,
    enabled: PropTypes.bool,
  }).isRequired,
}
